/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@stoneage/App/Store/Reducers";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaPodcastEvent } from "@stoneage/App/Utilities/DataLayer";
import Icon, { IconProps } from "@stoneage/MobiusOverrides/Icon";
import { color } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    links = "links",
    textColor = "textColor",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.links]: LinkModel[];
        [fields.textColor]: string;
        [fields.backgroundColor]: string;
    };
}
const enum icons {
    GraphicStitcher = "GraphicStitcher",
    GraphicSoundcloud = "GraphicSoundcloud",
    GraphicApplepodcast = "GraphicApplepodcast",
    GraphicSpotify = "GraphicSpotify",
}
const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.GraphicStitcher:
            return "GraphicStitcher";
        case icons.GraphicSoundcloud:
            return "GraphicSoundcloud";
        case icons.GraphicApplepodcast:
            return "GraphicApplepodcast";
        case icons.GraphicSpotify:
            return "GraphicSpotify";
        default:
            break;
    }

    return "";
};
interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
        iconColor: string;
    };
}
interface OwnProps extends WidgetProps {
    extendedStyles?: PodcastStyles;
}

export interface PodcastStyles {
    widgetGridContainer?: InjectableCss;
    leftContentContainer?: InjectableCss;
    rightContentContainer?: InjectableCss;
    titleLabel?: TypographyProps;
    label?: TypographyProps;
    iconsContainer?: InjectableCss;
    podcastIcon?: IconProps;
    icon?: IconProps;
    link: LinkPresentationProps;
}

export const podcastStyles: PodcastStyles = {
    widgetGridContainer: {
        css: css`
            padding-top: 20px;
        `,
    },
    leftContentContainer: {
        css: css`
            width: 81px;
            display: flex;
            justify-content: center;
        `,
    },
    rightContentContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    titleLabel: {
        variant: "h5",
        css: css`
            margin-bottom: 4px;
        `,
    },
    label: {
        css: css`
            & p {
                font-size: 12px;
                margin-bottom: 16px;
            }
        `,
    },
    iconsContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 16px 32px;
            padding-bottom: 15px;
        `,
    },
    link: {
        typographyProps: {
            color: "text.main",
        },
        css: css`
            margin-right: 5px;
            margin-bottom: 5px;
            &:nth-child(4n) {
                margin: 0;
            }
        `,
        color: "common.white",
    },
    icon: {
        width: 80,
        height: 24,
        css: css`
            &:hover,
            &:focus {
                color: ${color("primary.main")};
            }
        `,
    },
};

const Podcast: FC<OwnProps> = ({ id, fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(podcastStyles, extendedStyles));
    const links = fields.links && useGetLinks(fields.links, o => o.fields.destination);
    const { textColor, backgroundColor } = fields;
    const { customerNumber, podcastEvent, impersonation } = useAppSelector(state => ({
        customerNumber: getBillToCustomerNumber(state),
        podcastEvent: (state as xGoogleAnalyticsState).xGoogleAnalytics.podcastEvent,
        impersonation: trackAsImpersonation(state),
    }));
    
    return (
        <StyledWrapper
            css={css`
                display: flex;
                padding-top: 20px;
                background: ${backgroundColor || "transparent"};
            `}
        >
            <StyledWrapper {...styles.leftContentContainer}>
                <Icon src="IconFooterPodcast" {...styles.podcastIcon} size={35} />
            </StyledWrapper>
            <StyledWrapper {...styles.rightContentContainer}>
                <StyledWrapper>
                    <Typography className="title" {...styles.titleLabel} color={textColor || "common.white"}>
                        {fields.title}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography className="label-text" {...styles.label} color={textColor || "common.ui5"}>
                        {parse(fields.label, parserOptions)}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.iconsContainer}>
                    <StyledWrapper>
                        {links &&
                            links.map(
                                (link, index) =>
                                    link.url && (
                                        <Link
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            className="social-link"
                                            {...styles.link}
                                            href={link.url}
                                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                            onClick={() => podcastEvent && trackGaPodcastEvent(link.url!, impersonation, customerNumber)}
                                            aria-label={`podcast-link${index}`}
                                            typographyProps={{ color: textColor || "common.white" }}
                                        >
                                            <Icon
                                                src={GetIcon(fields.links[index].fields.icon)}
                                                {...styles.icon}
                                                color={fields.links[index].fields.iconColor || "common.white"}
                                            />
                                        </Link>
                                    ),
                            )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const podcast: WidgetModule = {
    component: Podcast,
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Industrial Theory Podcast",
                fieldType: "Translatable",
                tab: contentTab,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Available on these platforms.</p>",
                fieldType: "Translatable",
                tab: contentTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        displayName: "Select Icon",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Spotify", value: icons.GraphicSpotify },
                            { displayName: "Applepodcast", value: icons.GraphicApplepodcast },
                            { displayName: "Soundcloud", value: icons.GraphicSoundcloud },
                            { displayName: "Stitcher", value: icons.GraphicStitcher },
                        ],
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "iconColor",
                        displayName: "Icon Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
        ],
    },
};

export default podcast;
